import React from 'react';
import BrandedSpinner from './BrandedSpinner';

interface PageLoadingProps {
  /** Loading message to display below spinner */
  message?: string;
  /** Size of the spinner */
  size?: 'small' | 'medium' | 'large';
  /** Additional CSS classes */
  className?: string;
  /** Variant: 'full-page' | 'inline' | 'suspense' */
  variant?: 'full-page' | 'inline' | 'suspense';
}

/**
 * Unified loading component for consistent loading states across the application.
 * 
 * Variants:
 * - 'full-page': Full-screen centered loading (min-h-screen)
 * - 'inline': Inline loading within content areas (py-12)
 * - 'suspense': For Suspense fallbacks (min-h-screen, centered)
 * 
 * @example
 * // Full-page loading
 * if (loading) {
 *   return <PageLoading variant="full-page" message="Loading..." />;
 * }
 * 
 * // Inline loading
 * {isLoading && <PageLoading variant="inline" />}
 * 
 * // Suspense fallback
 * <Suspense fallback={<PageLoading variant="suspense" message="Loading page..." />}>
 *   <MyComponent />
 * </Suspense>
 */
const PageLoading: React.FC<PageLoadingProps> = ({
  message,
  size = 'medium',
  className = '',
  variant = 'full-page'
}) => {
  const sizeMap: Record<string, 'sm' | 'md' | 'lg'> = {
    small: 'sm',
    medium: 'md',
    large: 'lg'
  };

  const spinner = (
    <BrandedSpinner size={sizeMap[size]} />
  );

  // Full-page variant: min-h-screen, centered
  if (variant === 'full-page') {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 ${className}`}>
        <div className="flex flex-col items-center justify-center">
          {spinner}
          {message && (
            <p className="mt-6 text-sm font-medium text-gray-500 dark:text-gray-400 text-center animate-pulse">{message}</p>
          )}
        </div>
      </div>
    );
  }

  // Suspense variant: centered
  if (variant === 'suspense') {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 ${className}`}>
        <div className="flex flex-col items-center justify-center">
          {spinner}
          {message && (
            <p className="mt-6 text-sm font-medium text-gray-500 dark:text-gray-400 text-center animate-pulse">{message}</p>
          )}
        </div>
      </div>
    );
  }

  // Inline variant: py-12 for spacing within content
  return (
    <div className={`flex items-center justify-center py-12 ${className}`}>
      <div className="flex flex-col items-center justify-center">
        {spinner}
        {message && (
          <p className="mt-6 text-sm font-medium text-gray-500 dark:text-gray-400 text-center animate-pulse">{message}</p>
        )}
      </div>
    </div>
  );
};

export default PageLoading;

